/**
 * DEVELOPMENT / DEMO SEED for the Campus Representative Network.
 *
 * Everything in this file is clearly-marked development data used to exercise
 * the workflows. Production metrics begin from real records — no fabricated
 * representative counts, earnings or testimonials are surfaced as live figures.
 */
import { stamp } from '../utils/network'
import type {
  AuditEntry,
  Dispute,
  EarningEntry,
  FeatureFlags,
  FieldTask,
  FraudFlag,
  Incident,
  IntelReport,
  Lead,
  NetworkCampus,
  PerformanceEvent,
  Referral,
  Region,
  RepApplication,
  Representative,
  RewardRule,
  TrainingModule,
  VerificationSubmission,
} from '../types/network'

export const IS_DEMO_DATA = true

export const regions: Region[] = [
  { id: 'reg-sw', name: 'South West', country: 'Nigeria', currency: 'NGN', leadRepId: 'rep-004' },
  { id: 'reg-se', name: 'South East', country: 'Nigeria', currency: 'NGN', leadRepId: null },
]

export const networkCampuses: NetworkCampus[] = [
  {
    id: 'cmp-unilag',
    name: 'Campuzon UNILAG',
    institution: 'University of Lagos',
    institutionType: 'Federal university',
    regionId: 'reg-sw',
    city: 'Lagos',
    state: 'Lagos',
    country: 'Nigeria',
    coordinates: { lat: 6.5158, lng: 3.3966 },
    studentEstimate: 57000,
    active: true,
    published: true,
    recruitmentStatus: 'Active',
    campusLeadRepId: 'rep-002',
    transportNote: 'Danfo and keke along Akoka–Bariga axis. Third Mainland access at Iyana Oworo.',
    website: 'https://unilag.edu.ng',
    areas: [
      { id: 'area-akoka', name: 'Akoka', kind: 'Accommodation zone', areaLeadRepId: 'rep-001', minutesFromGate: 8 },
      { id: 'area-bariga', name: 'Bariga', kind: 'Accommodation zone', areaLeadRepId: null, minutesFromGate: 18 },
      { id: 'area-yaba', name: 'Yaba market strip', kind: 'Business corridor', areaLeadRepId: 'rep-003', minutesFromGate: 22 },
    ],
  },
  {
    id: 'cmp-futa',
    name: 'Campuzon FUTA',
    institution: 'Federal University of Technology, Akure',
    institutionType: 'Federal university of technology',
    regionId: 'reg-sw',
    city: 'Akure',
    state: 'Ondo',
    country: 'Nigeria',
    coordinates: { lat: 7.3013, lng: 5.1379 },
    studentEstimate: 24000,
    active: true,
    published: true,
    recruitmentStatus: 'Pilot',
    campusLeadRepId: null,
    transportNote: 'Shared taxis from South Gate to Ijoka and Oba Ile.',
    website: 'https://futa.edu.ng',
    areas: [
      { id: 'area-ijoka', name: 'Ijoka', kind: 'Accommodation zone', areaLeadRepId: null, minutesFromGate: 10 },
      { id: 'area-southgate', name: 'South Gate', kind: 'Business corridor', areaLeadRepId: null, minutesFromGate: 4 },
    ],
  },
  {
    id: 'cmp-oau',
    name: 'Campuzon OAU',
    institution: 'Obafemi Awolowo University',
    institutionType: 'Federal university',
    regionId: 'reg-sw',
    city: 'Ile-Ife',
    state: 'Osun',
    country: 'Nigeria',
    coordinates: { lat: 7.5171, lng: 4.5236 },
    studentEstimate: 35000,
    active: false,
    published: false,
    recruitmentStatus: 'Preparation',
    campusLeadRepId: null,
    transportNote: 'Not yet surveyed.',
    website: 'https://oauife.edu.ng',
    areas: [],
  },
]

export const representatives: Representative[] = [
  {
    id: 'rep-001',
    repCode: 'CR-UNILAG-014',
    name: 'Amarachi Obi',
    initials: 'AO',
    campusId: 'cmp-unilag',
    areaId: 'area-akoka',
    rank: 'Senior Representative',
    status: 'Active',
    supervisorRepId: 'rep-002',
    joinedOn: '4 Mar 2026',
    referralCode: 'CZ-AMARACHI',
    onboarding: [
      { key: 'terms', label: 'Representative agreement', detail: 'Scope of authority and conduct.', required: true, done: true },
      { key: 'conduct', label: 'Code of conduct', detail: 'What you may and may not represent.', required: true, done: true },
      { key: 'privacy', label: 'Privacy acknowledgement', detail: 'Handling landlord and student data.', required: true, done: true },
      { key: 'identity', label: 'Identity confirmation', detail: 'Student ID checked by the campus lead.', required: true, done: true },
      { key: 'payout', label: 'Payout details', detail: 'Held by the payment provider, not Campuzon.', required: true, done: true },
      { key: 'training', label: 'Core training', detail: 'Orientation and evidence standards.', required: true, done: true },
    ],
    trainingCompleted: ['trn-orientation', 'trn-evidence', 'trn-safety'],
    warnings: 0,
    historic: { verified: 23, referred: 41, businesses: 6 },
  },
  {
    id: 'rep-002',
    repCode: 'CR-UNILAG-001',
    name: 'Tunde Adeleke',
    initials: 'TA',
    campusId: 'cmp-unilag',
    areaId: null,
    rank: 'Campus Lead',
    status: 'Active',
    supervisorRepId: 'rep-004',
    joinedOn: '12 Jan 2026',
    referralCode: 'CZ-TUNDE',
    onboarding: [],
    trainingCompleted: ['trn-orientation', 'trn-evidence', 'trn-safety', 'trn-fraud', 'trn-leadership'],
    warnings: 0,
    historic: { verified: 58, referred: 96, businesses: 14 },
  },
  {
    id: 'rep-003',
    repCode: 'CR-UNILAG-022',
    name: 'Blessing Eze',
    initials: 'BE',
    campusId: 'cmp-unilag',
    areaId: 'area-yaba',
    rank: 'Area Lead',
    status: 'Probation',
    supervisorRepId: 'rep-002',
    joinedOn: '2 May 2026',
    referralCode: 'CZ-BLESSING',
    onboarding: [],
    trainingCompleted: ['trn-orientation', 'trn-evidence'],
    warnings: 1,
    historic: { verified: 11, referred: 8, businesses: 9 },
  },
  {
    id: 'rep-004',
    repCode: 'CR-SW-LEAD',
    name: 'Ifeoma Nwachukwu',
    initials: 'IN',
    campusId: 'cmp-unilag',
    areaId: null,
    rank: 'Regional Lead',
    status: 'Active',
    supervisorRepId: null,
    joinedOn: '3 Nov 2025',
    referralCode: 'CZ-IFEOMA',
    onboarding: [],
    trainingCompleted: ['trn-orientation', 'trn-evidence', 'trn-safety', 'trn-fraud', 'trn-leadership'],
    warnings: 0,
    historic: { verified: 40, referred: 120, businesses: 22 },
  },
  {
    id: 'rep-005',
    repCode: 'CR-FUTA-003',
    name: 'Segun Bakare',
    initials: 'SB',
    campusId: 'cmp-futa',
    areaId: 'area-ijoka',
    rank: 'Campus Representative',
    status: 'Pending onboarding',
    supervisorRepId: null,
    joinedOn: '11 Aug 2026',
    referralCode: 'CZ-SEGUN',
    onboarding: [
      { key: 'terms', label: 'Representative agreement', detail: 'Scope of authority and conduct.', required: true, done: true },
      { key: 'conduct', label: 'Code of conduct', detail: 'What you may and may not represent.', required: true, done: true },
      { key: 'privacy', label: 'Privacy acknowledgement', detail: 'Handling landlord and student data.', required: true, done: false },
      { key: 'identity', label: 'Identity confirmation', detail: 'Student ID checked by the campus lead.', required: true, done: false },
      { key: 'payout', label: 'Payout details', detail: 'Held by the payment provider, not Campuzon.', required: false, done: false },
      { key: 'training', label: 'Core training', detail: 'Orientation and evidence standards.', required: true, done: false },
    ],
    trainingCompleted: [],
    warnings: 0,
    historic: { verified: 0, referred: 0, businesses: 0 },
  },
]

/** The signed-in representative for the field console. */
export const CURRENT_REP_ID = 'rep-001'

export const applications: RepApplication[] = [
  {
    id: 'APP-2026-0184',
    submittedOn: '12 Aug 2026',
    status: 'Under review',
    fullName: 'Chinedu Okafor',
    email: 'chinedu.okafor@example.com',
    phone: '+234 80• ••• 4412',
    institution: 'University of Lagos',
    campusId: 'cmp-unilag',
    campusStatusAtApplication: 'Active',
    applicantStatus: 'Current student',
    programme: 'Estate Management',
    level: '300 level',
    graduationYear: '2028',
    location: { area: 'Bariga', city: 'Lagos', state: 'Lagos' },
    residentialArea: 'Bariga',
    weeklyHours: '10 – 15 hours',
    availableDays: ['Mon', 'Wed', 'Fri', 'Sat'],
    fieldPeriods: ['Morning', 'Afternoon'],
    transport: 'Public transport',
    hasSmartphone: true,
    hasCamera: true,
    connectivity: 'Mobile data, mostly reliable',
    experience: 'Ran a hostel-finding WhatsApp group for two sessions.',
    motivation: 'I already help friends find rooms. I would rather it be verified work.',
    preferredActivities: ['Property verification', 'Landlord onboarding'],
    consents: [
      { key: 'privacy', label: 'Privacy notice read', given: true },
      { key: 'contact', label: 'Consent to be contacted about this application', given: true },
      { key: 'terms', label: 'Programme terms accepted', given: true },
    ],
    documents: [
      {
        key: 'student-id',
        label: 'Student ID',
        hint: 'JPG, PNG or PDF, up to 5 MB.',
        required: true,
        fileName: 'unilag-id-front.jpg',
        sizeBytes: 412_000,
        mimeType: 'image/jpeg',
        previewUrl: null,
        state: 'Under review',
        reviewNote: null,
      },
      {
        key: 'photo',
        label: 'Profile photograph',
        hint: 'A clear photograph of your face. JPG or PNG, up to 5 MB.',
        required: true,
        fileName: 'chinedu-passport.png',
        sizeBytes: 288_000,
        mimeType: 'image/png',
        previewUrl: null,
        state: 'Verified',
        reviewNote: null,
      },
    ],
    internalNotes: [
      { at: '13 Aug 2026', actor: 'Ops — K. Yusuf', body: 'Bariga coverage is thin. Worth shortlisting.' },
    ],
  },
  {
    id: 'APP-2026-0190',
    submittedOn: '13 Aug 2026',
    status: 'Submitted',
    fullName: 'Halima Sani',
    email: 'halima.sani@example.com',
    phone: '+234 70• ••• 9077',
    institution: 'Federal University of Technology, Akure',
    campusId: 'cmp-futa',
    campusStatusAtApplication: 'Pilot',
    applicantStatus: 'Current student',
    programme: 'Urban and Regional Planning',
    level: '400 level',
    graduationYear: '2027',
    location: { area: 'Ijoka', city: 'Akure', state: 'Ondo' },
    residentialArea: 'Ijoka',
    weeklyHours: '5 – 10 hours',
    availableDays: ['Tue', 'Thu', 'Sat', 'Sun'],
    fieldPeriods: ['Afternoon'],
    transport: 'Motorcycle',
    hasSmartphone: true,
    hasCamera: true,
    connectivity: 'Mobile data, patchy in Ijoka',
    experience: 'Course rep for two sessions.',
    motivation: 'FUTA accommodation information is scattered and often wrong.',
    preferredActivities: ['Campus intelligence', 'Student referrals'],
    consents: [
      { key: 'privacy', label: 'Privacy notice read', given: true },
      { key: 'contact', label: 'Consent to be contacted about this application', given: true },
      { key: 'terms', label: 'Programme terms accepted', given: true },
    ],
    documents: [
      {
        key: 'student-id',
        label: 'Student ID',
        hint: 'JPG, PNG or PDF, up to 5 MB.',
        required: true,
        fileName: 'futa-id.pdf',
        sizeBytes: 640_000,
        mimeType: 'application/pdf',
        previewUrl: null,
        state: 'Re-upload required',
        reviewNote: 'The expiry date is cut off. Please send the whole card.',
      },
      {
        key: 'photo',
        label: 'Profile photograph',
        hint: 'A clear photograph of your face. JPG or PNG, up to 5 MB.',
        required: true,
        fileName: null,
        sizeBytes: null,
        mimeType: null,
        previewUrl: null,
        state: 'Not provided',
        reviewNote: null,
      },
    ],
    internalNotes: [],
  },
]

export const rewardRules: RewardRule[] = [
  {
    id: 'rr-verify',
    label: 'Property verification approved',
    activity: 'Verify property',
    kind: 'Fixed',
    amount: 2500,
    currency: 'NGN',
    campusId: null,
    requiresApproval: true,
    effectiveFrom: '1 Jan 2026',
    effectiveTo: null,
    monthlyCap: 60000,
    active: true,
  },
  {
    id: 'rr-reverify',
    label: 'Reverification approved',
    activity: 'Reverify property',
    kind: 'Fixed',
    amount: 1200,
    currency: 'NGN',
    campusId: null,
    requiresApproval: true,
    effectiveFrom: '1 Jan 2026',
    effectiveTo: null,
    monthlyCap: 30000,
    active: true,
  },
  {
    id: 'rr-business',
    label: 'Business onboarded and verified',
    activity: 'Onboard business',
    kind: 'Fixed',
    amount: 3000,
    currency: 'NGN',
    campusId: null,
    requiresApproval: true,
    effectiveFrom: '1 Mar 2026',
    effectiveTo: null,
    monthlyCap: null,
    active: true,
  },
  {
    id: 'rr-referral',
    label: 'Student referral reached first meaningful action',
    activity: 'Referral',
    kind: 'Referral',
    amount: 400,
    currency: 'NGN',
    campusId: null,
    requiresApproval: false,
    effectiveFrom: '1 Feb 2026',
    effectiveTo: null,
    monthlyCap: 20000,
    active: true,
  },
  {
    id: 'rr-booking',
    label: 'Commission on a booking from a property you verified',
    activity: 'Housing booking',
    kind: 'Commission',
    amount: 0,
    percentage: 2,
    currency: 'NGN',
    campusId: null,
    requiresApproval: true,
    effectiveFrom: '1 Apr 2026',
    effectiveTo: null,
    monthlyCap: null,
    active: true,
  },
  {
    id: 'rr-intel',
    label: 'Campus intelligence published',
    activity: 'Collect campus intelligence',
    kind: 'Fixed',
    amount: 600,
    currency: 'NGN',
    campusId: null,
    requiresApproval: true,
    effectiveFrom: '1 Jun 2026',
    effectiveTo: null,
    monthlyCap: 12000,
    active: true,
  },
]

const PROPERTY_EVIDENCE = [
  { key: 'gps', label: 'Location captured at the property', kind: 'gps' as const, required: true },
  { key: 'exterior', label: 'Exterior photograph', kind: 'photo' as const, required: true },
  { key: 'interior', label: 'Interior photograph', kind: 'photo' as const, required: true },
  { key: 'facilities', label: 'Facilities photograph', kind: 'photo' as const, required: false },
  { key: 'propertyType', label: 'Property and room type', kind: 'field' as const, required: true },
  { key: 'rooms', label: 'Rooms and occupancy', kind: 'field' as const, required: true },
  { key: 'rent', label: 'Rent, deposit and service charge', kind: 'field' as const, required: true },
  { key: 'utilities', label: 'Electricity, water, internet', kind: 'field' as const, required: true },
  { key: 'owner', label: 'Owner or agent, and their relationship to the property', kind: 'field' as const, required: true },
  { key: 'availability', label: 'Availability', kind: 'field' as const, required: true },
  { key: 'declaration', label: 'Representative declaration', kind: 'declaration' as const, required: true },
]

export const fieldTasks: FieldTask[] = [
  {
    id: 'TSK-4471',
    type: 'Verify property',
    title: 'Verify Akoka Court, Block B',
    detail: '12-room hostel behind the Akoka gate. Landlord has agreed to an inspection window.',
    campusId: 'cmp-unilag',
    areaId: 'area-akoka',
    priority: 'Priority',
    status: 'Accepted',
    assignedRepId: 'rep-001',
    createdBy: 'Ops — K. Yusuf',
    createdOn: '13 Aug 2026',
    dueOn: '16 Aug 2026',
    rewardRuleId: 'rr-verify',
    minRank: 'Campus Representative',
    requiresTraining: ['trn-evidence'],
    evidence: PROPERTY_EVIDENCE,
    propertyRef: 'PRP-AKOKA-COURT-B',
    expectedLocation: { lat: 6.5171, lng: 3.3922 },
    history: [
      { at: '13 Aug 2026, 09:12', actor: 'Ops — K. Yusuf', label: 'Task created and assigned' },
      { at: '13 Aug 2026, 10:04', actor: 'Amarachi Obi', label: 'Accepted' },
    ],
  },
  {
    id: 'TSK-4483',
    type: 'Confirm availability',
    title: 'Confirm three listed rooms at Sunview Lodge',
    detail: 'Listing claims three vacancies. Two students reported being told it was full.',
    campusId: 'cmp-unilag',
    areaId: 'area-akoka',
    priority: 'Urgent',
    status: 'Open',
    assignedRepId: null,
    createdBy: 'Trust and safety',
    createdOn: '14 Aug 2026',
    dueOn: '15 Aug 2026',
    rewardRuleId: 'rr-reverify',
    minRank: 'Campus Representative',
    requiresTraining: ['trn-evidence'],
    evidence: [
      { key: 'gps', label: 'Location captured at the property', kind: 'gps', required: true },
      { key: 'exterior', label: 'Exterior photograph', kind: 'photo', required: true },
      { key: 'availability', label: 'Rooms actually available today', kind: 'field', required: true },
      { key: 'owner', label: 'Who confirmed it, and their role', kind: 'field', required: true },
      { key: 'declaration', label: 'Representative declaration', kind: 'declaration', required: true },
    ],
    propertyRef: 'PRP-SUNVIEW-LODGE',
    expectedLocation: { lat: 6.5194, lng: 3.3888 },
    history: [{ at: '14 Aug 2026, 07:40', actor: 'Trust and safety', label: 'Task created from a student report' }],
  },
  {
    id: 'TSK-4488',
    type: 'Investigate report',
    title: 'Investigate a suspected duplicate listing',
    detail: 'Two listings share the same photographs under different agent names.',
    campusId: 'cmp-unilag',
    areaId: 'area-bariga',
    priority: 'Priority',
    status: 'Open',
    assignedRepId: null,
    createdBy: 'Trust and safety',
    createdOn: '14 Aug 2026',
    dueOn: '18 Aug 2026',
    rewardRuleId: 'rr-reverify',
    minRank: 'Area Lead',
    requiresTraining: ['trn-evidence', 'trn-fraud'],
    evidence: [
      { key: 'gps', label: 'Location captured at each address', kind: 'gps', required: true },
      { key: 'exterior', label: 'Exterior photograph', kind: 'photo', required: true },
      { key: 'owner', label: 'Who claims to manage it', kind: 'field', required: true },
      { key: 'declaration', label: 'Representative declaration', kind: 'declaration', required: true },
    ],
    propertyRef: 'PRP-BARIGA-DUP',
    expectedLocation: { lat: 6.5372, lng: 3.3902 },
    history: [{ at: '14 Aug 2026, 08:02', actor: 'Trust and safety', label: 'Task created' }],
  },
  {
    id: 'TSK-4402',
    type: 'Onboard business',
    title: 'Onboard Mama Nkechi Kitchen',
    detail: 'Food vendor on the Akoka strip, already popular with students.',
    campusId: 'cmp-unilag',
    areaId: 'area-akoka',
    priority: 'Routine',
    status: 'Approved',
    assignedRepId: 'rep-001',
    createdBy: 'Ops — K. Yusuf',
    createdOn: '2 Aug 2026',
    dueOn: '9 Aug 2026',
    rewardRuleId: 'rr-business',
    minRank: 'Campus Representative',
    requiresTraining: ['trn-orientation'],
    evidence: [
      { key: 'gps', label: 'Location captured at the business', kind: 'gps', required: true },
      { key: 'exterior', label: 'Storefront photograph', kind: 'photo', required: true },
      { key: 'owner', label: 'Owner name and role', kind: 'field', required: true },
      { key: 'declaration', label: 'Representative declaration', kind: 'declaration', required: true },
    ],
    reviewerId: 'Reviewer — D. Aliu',
    reviewNotes: 'Clear evidence. Business profile created and queued for verification.',
    history: [
      { at: '2 Aug 2026', actor: 'Ops — K. Yusuf', label: 'Task created' },
      { at: '6 Aug 2026', actor: 'Amarachi Obi', label: 'Submitted' },
      { at: '7 Aug 2026', actor: 'Reviewer — D. Aliu', label: 'Approved' },
    ],
  },
  {
    id: 'TSK-4390',
    type: 'Verify property',
    title: 'Verify Bariga Heights annexe',
    detail: 'Six self-contained units. Previous verification expired in July.',
    campusId: 'cmp-unilag',
    areaId: 'area-bariga',
    priority: 'Routine',
    status: 'Rework required',
    assignedRepId: 'rep-001',
    createdBy: 'Ops — K. Yusuf',
    createdOn: '28 Jul 2026',
    dueOn: '17 Aug 2026',
    rewardRuleId: 'rr-verify',
    minRank: 'Campus Representative',
    requiresTraining: ['trn-evidence'],
    evidence: PROPERTY_EVIDENCE,
    propertyRef: 'PRP-BARIGA-HEIGHTS',
    expectedLocation: { lat: 6.5361, lng: 3.3874 },
    reviewerId: 'Reviewer — D. Aliu',
    reviewNotes:
      'Interior photograph shows only the corridor, and the rent figure contradicts what the agent told the trust team. Please recapture.',
    history: [
      { at: '28 Jul 2026', actor: 'Ops — K. Yusuf', label: 'Task created' },
      { at: '3 Aug 2026', actor: 'Amarachi Obi', label: 'Submitted' },
      { at: '5 Aug 2026', actor: 'Reviewer — D. Aliu', label: 'Rework required' },
    ],
  },
  {
    id: 'TSK-4495',
    type: 'Collect campus intelligence',
    title: 'Rent movement on the Bariga axis',
    detail: 'Capture asking rents for shared rooms ahead of the new session.',
    campusId: 'cmp-unilag',
    areaId: 'area-bariga',
    priority: 'Routine',
    status: 'Open',
    assignedRepId: null,
    createdBy: 'Ops — K. Yusuf',
    createdOn: '14 Aug 2026',
    dueOn: '22 Aug 2026',
    rewardRuleId: 'rr-intel',
    minRank: 'Campus Representative',
    requiresTraining: ['trn-orientation'],
    evidence: [
      { key: 'observation', label: 'What you observed', kind: 'field', required: true },
      { key: 'prices', label: 'Asking rents seen', kind: 'field', required: true },
      { key: 'declaration', label: 'Representative declaration', kind: 'declaration', required: true },
    ],
    history: [{ at: '14 Aug 2026', actor: 'Ops — K. Yusuf', label: 'Task created' }],
  },
]

export const verifications: VerificationSubmission[] = [
  {
    id: 'VER-3301',
    taskId: 'TSK-4402',
    repId: 'rep-001',
    propertyRef: 'BUS-MAMA-NKECHI',
    submittedOn: '6 Aug 2026',
    status: 'Approved',
    evidence: {
      gps: { lat: 6.5169, lng: 3.3931, accuracyM: 18, at: stamp(-11520) },
      photos: [{ key: 'exterior', label: 'Storefront', at: stamp(-11525), dataUrl: 'demo:storefront' }],
      declaration: true,
      fields: { owner: 'Nkechi Umeh — owner', availability: 'Open daily' },
    },
    distanceFromExpectedM: 42,
    flags: [],
    reviewerId: 'Reviewer — D. Aliu',
    reviewNotes: 'Clear evidence.',
    decidedOn: '7 Aug 2026',
    verifiedUntil: '7 Feb 2027',
    syncState: 'Synced',
  },
  {
    id: 'VER-3288',
    taskId: 'TSK-4390',
    repId: 'rep-001',
    propertyRef: 'PRP-BARIGA-HEIGHTS',
    submittedOn: '3 Aug 2026',
    status: 'Rework required',
    evidence: {
      gps: { lat: 6.5359, lng: 3.3871, accuracyM: 34, at: stamp(-15840) },
      photos: [
        { key: 'exterior', label: 'Exterior', at: stamp(-15845), dataUrl: 'demo:bariga-ext' },
        { key: 'interior', label: 'Corridor', at: stamp(-15843), dataUrl: 'demo:bariga-int' },
      ],
      declaration: true,
      fields: { rent: '₦280,000 per session', availability: 'Two units free' },
    },
    distanceFromExpectedM: 31,
    flags: [],
    reviewerId: 'Reviewer — D. Aliu',
    reviewNotes: 'Interior photograph shows only the corridor.',
    decidedOn: '5 Aug 2026',
    syncState: 'Synced',
  },
  {
    id: 'VER-3312',
    taskId: 'TSK-4471',
    repId: 'rep-003',
    propertyRef: 'PRP-YABA-ANNEX',
    submittedOn: '13 Aug 2026',
    status: 'Under review',
    evidence: {
      gps: { lat: 6.5083, lng: 3.3714, accuracyM: 210, at: stamp(-1200) },
      photos: [{ key: 'exterior', label: 'Exterior', at: stamp(-4000), dataUrl: 'demo:yaba-ext' }],
      declaration: true,
      fields: { rent: '₦190,000 per session', availability: 'One room' },
    },
    distanceFromExpectedM: 1840,
    flags: [
      'Captured 1840m from the expected property location, outside the 250m radius',
      'Low location accuracy (±210m)',
      'Media timestamp is more than 45 minutes from the location capture',
    ],
    syncState: 'Synced',
  },
]

export const earnings: EarningEntry[] = [
  {
    id: 'ERN-8801',
    repId: 'rep-001',
    ruleId: 'rr-business',
    activity: 'Business onboarded — Mama Nkechi Kitchen',
    sourceRef: 'TSK-4402',
    grossAmount: 3000,
    adjustment: 0,
    currency: 'NGN',
    status: 'Paid',
    createdOn: '7 Aug 2026',
    approvedOn: '7 Aug 2026',
    approvedBy: 'Finance — R. Okoro',
    paidOn: '9 Aug 2026',
  },
  {
    id: 'ERN-8776',
    repId: 'rep-001',
    ruleId: 'rr-verify',
    activity: 'Property verification — Akoka Rise',
    sourceRef: 'TSK-4361',
    grossAmount: 2500,
    adjustment: 0,
    currency: 'NGN',
    status: 'Approved',
    createdOn: '1 Aug 2026',
    approvedOn: '2 Aug 2026',
    approvedBy: 'Finance — R. Okoro',
    paidOn: null,
  },
  {
    id: 'ERN-8802',
    repId: 'rep-001',
    ruleId: 'rr-referral',
    activity: '4 referrals reached first meaningful action',
    sourceRef: 'REF-batch-Aug',
    grossAmount: 1600,
    adjustment: 0,
    currency: 'NGN',
    status: 'Approved',
    createdOn: '10 Aug 2026',
    approvedOn: '10 Aug 2026',
    approvedBy: 'Automatic — rule does not require approval',
    paidOn: null,
  },
  {
    id: 'ERN-8745',
    repId: 'rep-001',
    ruleId: 'rr-verify',
    activity: 'Property verification — Sunview Lodge (later disputed by the landlord)',
    sourceRef: 'TSK-4302',
    grossAmount: 2500,
    adjustment: 0,
    currency: 'NGN',
    status: 'Reversed',
    createdOn: '18 Jul 2026',
    approvedOn: '19 Jul 2026',
    approvedBy: 'Finance — R. Okoro',
    paidOn: null,
    reason: 'Verification withdrawn after the landlord disputed the recorded rent.',
  },
  {
    id: 'ERN-8746',
    repId: 'rep-001',
    ruleId: 'rr-verify',
    activity: 'Reversal of ERN-8745',
    sourceRef: 'TSK-4302',
    grossAmount: -2500,
    adjustment: 0,
    currency: 'NGN',
    status: 'Approved',
    createdOn: '24 Jul 2026',
    approvedOn: '24 Jul 2026',
    approvedBy: 'Finance — R. Okoro',
    paidOn: null,
    reversalOf: 'ERN-8745',
    reason: 'Adjustment entry. The original record is preserved unchanged.',
  },
  {
    id: 'ERN-8811',
    repId: 'rep-003',
    ruleId: 'rr-verify',
    activity: 'Property verification — Yaba annexe',
    sourceRef: 'TSK-4471',
    grossAmount: 2500,
    adjustment: 0,
    currency: 'NGN',
    status: 'Pending',
    createdOn: '13 Aug 2026',
    approvedOn: null,
    approvedBy: null,
    paidOn: null,
  },
]

export const referrals: Referral[] = [
  { id: 'REF-1', repId: 'rep-001', code: 'CZ-AMARACHI', stage: 'Transacted', createdOn: '2 Aug 2026', studentLabel: '@dami••', attributed: true, flag: null },
  { id: 'REF-2', repId: 'rep-001', code: 'CZ-AMARACHI', stage: 'First action', createdOn: '5 Aug 2026', studentLabel: '@kene••', attributed: true, flag: null },
  { id: 'REF-3', repId: 'rep-001', code: 'CZ-AMARACHI', stage: 'Verified account', createdOn: '8 Aug 2026', studentLabel: '@tobi••', attributed: true, flag: null },
  { id: 'REF-4', repId: 'rep-001', code: 'CZ-AMARACHI', stage: 'Registered', createdOn: '11 Aug 2026', studentLabel: '@ada••', attributed: true, flag: null },
  { id: 'REF-5', repId: 'rep-001', code: 'CZ-AMARACHI', stage: 'Click', createdOn: '13 Aug 2026', studentLabel: 'Anonymous visitor', attributed: false, flag: null },
  {
    id: 'REF-6',
    repId: 'rep-003',
    code: 'CZ-BLESSING',
    stage: 'Registered',
    createdOn: '12 Aug 2026',
    studentLabel: '@bles••',
    attributed: false,
    flag: 'Self-referral suspected — same device as the representative account',
  },
]

export const leads: Lead[] = [
  {
    id: 'LEAD-221',
    kind: 'Landlord',
    name: 'Mr. Ogundele',
    contactMasked: '+234 80• ••• 3319',
    campusId: 'cmp-unilag',
    areaId: 'area-akoka',
    repId: 'rep-001',
    stage: 'Interested',
    notes: 'Owns two buildings on Akoka Road. Wants to list before the session starts.',
    followUpOn: '17 Aug 2026',
    createdOn: '11 Aug 2026',
  },
  {
    id: 'LEAD-230',
    kind: 'Business',
    name: 'Crystal Laundry',
    contactMasked: '+234 81• ••• 7702',
    campusId: 'cmp-unilag',
    areaId: 'area-akoka',
    repId: 'rep-001',
    stage: 'Onboarding',
    category: 'Laundry',
    notes: 'Wants student pricing listed. Needs a CAC document for verification.',
    followUpOn: '16 Aug 2026',
    createdOn: '9 Aug 2026',
  },
  {
    id: 'LEAD-198',
    kind: 'Hostel',
    name: 'Peace Haven Hostel',
    contactMasked: '+234 90• ••• 1140',
    campusId: 'cmp-unilag',
    areaId: 'area-bariga',
    repId: 'rep-003',
    stage: 'Verified',
    notes: 'Converted. 18 beds now listed.',
    followUpOn: '—',
    createdOn: '21 Jul 2026',
  },
]

export const intelReports: IntelReport[] = [
  {
    id: 'INT-771',
    campusId: 'cmp-unilag',
    areaId: 'area-akoka',
    category: 'Prices',
    observation:
      'Shared-room asking rents on Akoka Road moved from ₦180k to about ₦220k per session ahead of the new intake.',
    repId: 'rep-001',
    observedOn: '10 Aug 2026',
    confidence: 'High',
    status: 'Published',
    expiresOn: '31 Oct 2026',
    hasEvidence: true,
  },
  {
    id: 'INT-780',
    campusId: 'cmp-unilag',
    areaId: 'area-bariga',
    category: 'Transport',
    observation: 'Keke fare from Bariga junction to the gate is now ₦200, up from ₦150.',
    repId: 'rep-001',
    observedOn: '13 Aug 2026',
    confidence: 'Medium',
    status: 'Under review',
    expiresOn: '30 Sep 2026',
    hasEvidence: false,
  },
  {
    id: 'INT-762',
    campusId: 'cmp-unilag',
    areaId: 'area-yaba',
    category: 'Internet',
    observation: 'New fibre provider covering the Yaba strip, advertising student plans.',
    repId: 'rep-003',
    observedOn: '4 Aug 2026',
    confidence: 'Low',
    status: 'Submitted',
    expiresOn: null,
    hasEvidence: false,
  },
]

export const trainingModules: TrainingModule[] = [
  {
    id: 'trn-orientation',
    title: 'Campuzon orientation',
    detail: 'What the network is for, what a representative may and may not represent.',
    minutes: 15,
    required: true,
    unlocks: ['Collect campus intelligence', 'Onboard business'],
    quiz: [
      {
        question: 'A landlord asks you to confirm you work for the university. What do you say?',
        options: [
          'That you are a Campuzon representative and hold no institutional authority',
          'That you work with the university on accommodation',
          'Nothing, and let them assume',
        ],
        answerIndex: 0,
      },
    ],
  },
  {
    id: 'trn-evidence',
    title: 'Evidence standards',
    detail: 'What a reviewer needs to see, and why a photograph alone is never enough.',
    minutes: 25,
    required: true,
    unlocks: ['Verify property', 'Reverify property', 'Confirm availability'],
    quiz: [
      {
        question: 'You cannot get inside a building. What is the correct action?',
        options: [
          'Submit exterior photographs and mark the interior as not captured',
          'Reuse an interior photograph from a similar building',
          'Mark the property verified anyway',
        ],
        answerIndex: 0,
      },
      {
        question: 'When must location be captured?',
        options: ['While standing at the property', 'Any time that day', 'Before you set out'],
        answerIndex: 0,
      },
    ],
  },
  {
    id: 'trn-safety',
    title: 'Field safety',
    detail: 'Check-in, unsafe locations, and when to walk away.',
    minutes: 20,
    required: true,
    unlocks: [],
    quiz: [
      {
        question: 'An agent insists you enter an occupied room alone at night. What do you do?',
        options: [
          'Decline, leave, and flag the location as unsafe',
          'Go in quickly to finish the task',
          'Ask a student to come with you',
        ],
        answerIndex: 0,
      },
    ],
  },
  {
    id: 'trn-fraud',
    title: 'Fraud awareness',
    detail: 'Duplicate listings, fake agents, advance-fee patterns.',
    minutes: 20,
    required: false,
    unlocks: ['Investigate report'],
    quiz: [
      {
        question: 'Two listings share identical photographs. What is that?',
        options: [
          'A duplicate-listing signal worth reporting',
          'Normal, agents share photographs',
          'Proof of fraud you should announce publicly',
        ],
        answerIndex: 0,
      },
    ],
  },
  {
    id: 'trn-leadership',
    title: 'Leading a campus team',
    detail: 'Reviewing others’ work, escalation, and handling a team member’s dispute.',
    minutes: 30,
    required: false,
    unlocks: [],
    quiz: [
      {
        question: 'A representative you supervise disputes a rejected verification. What do you do?',
        options: [
          'Send it to the dispute queue with your notes',
          'Overturn the rejection yourself',
          'Tell them to accept it',
        ],
        answerIndex: 0,
      },
    ],
  },
]

export const performanceEvents: PerformanceEvent[] = [
  { id: 'PE-1', repId: 'rep-001', at: '7 Aug 2026', kind: 'Verification approved', weightNote: 'Accuracy', delta: 2 },
  { id: 'PE-2', repId: 'rep-001', at: '5 Aug 2026', kind: 'Verification reworked', weightNote: 'Accuracy', delta: -3 },
  { id: 'PE-3', repId: 'rep-001', at: '10 Aug 2026', kind: 'Referral converted', weightNote: 'Referral quality', delta: 1 },
  { id: 'PE-4', repId: 'rep-003', at: '12 Aug 2026', kind: 'Policy warning', weightNote: 'Conduct', delta: -8 },
]

export const incidents: Incident[] = [
  {
    id: 'INC-104',
    repId: 'rep-003',
    campusId: 'cmp-unilag',
    kind: 'Unsafe property',
    detail: 'Exposed wiring in a stairwell at a listed building on the Yaba strip.',
    reportedOn: '12 Aug 2026',
    confidential: false,
    status: 'Escalated',
    handledBy: 'Trust and safety',
  },
  {
    id: 'INC-106',
    repId: 'rep-001',
    campusId: 'cmp-unilag',
    kind: 'Suspicious agent',
    detail: 'Agent asked for a ₦20,000 “inspection fee” before allowing entry.',
    reportedOn: '13 Aug 2026',
    confidential: true,
    status: 'Open',
    handledBy: null,
  },
]

export const fraudFlags: FraudFlag[] = [
  {
    id: 'FRD-51',
    repId: 'rep-003',
    signal: 'Self-referral',
    detail: 'A referred account registered from the same device as the representative account.',
    severity: 'Medium',
    raisedOn: '12 Aug 2026',
    state: 'Awaiting review',
  },
  {
    id: 'FRD-52',
    repId: 'rep-003',
    signal: 'Location inconsistency',
    detail: 'Submission captured 1.8km from the expected property, with poor accuracy.',
    severity: 'High',
    raisedOn: '13 Aug 2026',
    state: 'Awaiting review',
  },
]

export const disputes: Dispute[] = [
  {
    id: 'DSP-31',
    repId: 'rep-001',
    about: 'Rejected verification',
    ref: 'VER-3288',
    argument:
      'The corridor was the only interior the caretaker would allow. I recorded that in the notes.',
    raisedOn: '6 Aug 2026',
    state: 'Under review',
    decision: null,
  },
]

export const auditSeed: AuditEntry[] = [
  {
    id: 'AUD-9001',
    at: '13 Aug 2026, 14:22',
    actor: 'Ops — K. Yusuf',
    action: 'Task created and assigned',
    entity: 'TSK-4471',
    from: null,
    to: 'Assigned to CR-UNILAG-014',
    reason: 'Landlord agreed to an inspection window',
  },
  {
    id: 'AUD-8994',
    at: '12 Aug 2026, 16:03',
    actor: 'Ops — K. Yusuf',
    action: 'Representative placed on probation',
    entity: 'CR-UNILAG-022',
    from: 'Active',
    to: 'Probation',
    reason: 'Two flagged submissions inside a fortnight',
  },
  {
    id: 'AUD-8990',
    at: '9 Aug 2026, 11:40',
    actor: 'Finance — R. Okoro',
    action: 'Payout batch released',
    entity: 'PAY-2026-08-A',
    from: 'Approved',
    to: 'Paid',
    reason: 'Monthly settlement window',
  },
]

export const defaultFlags: FeatureFlags = {
  representative_network: true,
  property_verification: true,
  business_onboarding: true,
  campus_intelligence: true,
  referral_rewards: true,
  payouts: false,
  leadership: true,
  offline_mode: true,
  campus_map: true,
}
